import React, { useCallback, useEffect, useMemo } from "react";

import { useIsExpandedContext } from "components/BudgetBuilder/Overview/OverviewLineItemsList/OverviewLineItemsList";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Table from "components/Table";
import { addDirectCosts, getDirectCostSubtotal, getLineItemsDirectCostTotal } from "utils/budgetBuilder";

import styles from "./styles.css";

const getChildrenCostData = (categoryData, numberOfPeriods) => {
	let result = {};
	for (let i = 0; i < categoryData.length; i++) {
		let category = categoryData[i];
		let children = category?.items || [];
		children.forEach(child => {
			let key = child.hash;
			let childData = result[key] || getDefaultSponsoredCostsList(numberOfPeriods, child.name);
			childData[i] = child;
			result[key] = childData;
		});
	}

	return result;
};

const getDefaultSponsoredCostsList = (numberOfPeriods, name) => Array.from(Array(numberOfPeriods).keys())
	.map(() => ({ name }));

export const OverviewSponsoredCostsRow = ({ categoryData, hash, periods }) => {
	const { currencyFormatter } = useTenantFormatter();
	const { isExpandedState: { [hash]: isExpanded }, isExpandedDispatch } = useIsExpandedContext();
	const setIsExpanded = useCallback(
		isExpanded => isExpandedDispatch({ type: hash, isExpanded }),
		[ hash, isExpandedDispatch ],
	);
	useEffect(() => {
		isExpandedDispatch({ type: hash, isExpanded: true });
	}, [ hash, isExpandedDispatch ]);
	const handleIsExpandedChange = isExpanded => {
		setIsExpanded(isExpanded);
	};
	const name = categoryData?.[0]?.value;
	const childData = useMemo(
		() => getChildrenCostData(categoryData, periods.length),
		[ categoryData, periods.length ],
	);

	const total = useMemo(
		() => getLineItemsDirectCostTotal(categoryData),
		[ categoryData ]
	);

	return (
		<>
			{isExpanded && <>
				<Table.Row>
					<Table.Cell className={`${styles.cellAlignLeft} ${styles.expandCell}`}>
						<span>{name}</span>
						{isExpanded && <div className={styles.expandIcon} onClick={() => handleIsExpandedChange(!isExpanded)}>
							<i className="fa fa-caret-down" aria-hidden="true" />
						</div>}
					</Table.Cell>
					{periods?.map((period, p) => (
						<Table.Cell key={`period-${p}`}>
							<div className={styles.cellAlignRight}>
								<span className={styles.bold}>{"Amount"}</span>
							</div>
						</Table.Cell>
					))}
					<Table.Cell className={styles.cellAlignRight} />
				</Table.Row>
				{Object.entries(childData)?.map(([ i, items ]) => (
					<Table.Row key={`item-${i}`}>
						<Table.Cell className={styles.cellAlignLeft}>
							<span>{items[0]?.value}</span>
						</Table.Cell>
						{items.map((item, p) => (
							<Table.Cell key={`period-${i}-${p}`}>
								<div className={styles.cellAlignRight}>
									<span>{currencyFormatter(item.directCost, 0)}</span>
								</div>
							</Table.Cell>
						))}
						<Table.Cell className={styles.cellAlignRight}>
							<span className={styles.bold}>{currencyFormatter(addDirectCosts(items))}</span>
						</Table.Cell>
					</Table.Row>
				))}
			</>}
			<Table.Row>
				<Table.Cell className={`${styles.cellAlignLeft} ${!isExpanded ? styles.expandCell : undefined}`}>
					<span className={isExpanded ? styles.bold : undefined}>{`${name} ${isExpanded ? "Total" : ""}`}</span>
					{!isExpanded && <div className={styles.expandIcon} onClick={() => handleIsExpandedChange(!isExpanded)}>
						<i className="fa fa-caret-right" aria-hidden="true" />
					</div>}
				</Table.Cell>
				{Object.entries(categoryData).map(([ p, category ]) => (
					<Table.Cell className={styles.cellAlignRight} key={`period-${p}`}>
						<span className={isExpanded ? styles.bold : undefined}>
							{currencyFormatter(getDirectCostSubtotal(category))}
						</span>
					</Table.Cell>
				))}
				<Table.Cell className={styles.cellAlignRight}>
					<span className={styles.bold}>{currencyFormatter(total)}</span>
				</Table.Cell>
			</Table.Row>
		</>
	);
};
